import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./submit-service-request-page.module.scss";
import { CustomerLoader } from "../../components/customer-loader";
import { Button } from "../../components/button";
import { FailedToLoadData } from "../../../../common/components/failed-to-load-data/FailedToLoadData";
import { CardFrame } from "../../../../common/components/card-frame/CardFrame";
import { DynamicForm } from "../../components/dynamic-form";
import { PaymentProcessor } from "../../components/payment-processor";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import SubmitRequestAppBar from "./components/SubmitRequestAppBar";
import ServiceRequestHeader from "./components/ServiceRequestHeader";
import SubmitRequestConfirmationStep from "./steps/SubmitRequestConfirmationStep";
import SubmitRequestSuccessStep from "./steps/SubmitRequestSuccessStep";
import ServiceRequestPaymentModal from "./payment/ServiceRequestPaymentModal";
import { useSubmitServiceRequestPage } from "./useSubmitServiceRequestPage";

type SubmitServiceRequestLocationState = { serviceRequestId?: string } | null;

export const SubmitServiceRequestPage: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const serviceRequestId = (location.state as SubmitServiceRequestLocationState)?.serviceRequestId;

	const {
		pageState,
		serviceRequest,
		currentStep,
		currentSection,
		currentSectionDefinition,
		currentSectionValues,
		allSectionValues,
		isSubmitted,
		totalSteps,
		activeStep,
		tagName,
		sectionTitle,
		headerSubtitle,
		bottomButtonLabel,
		handleValuesChange,
		goBack,
		goNext,
		price,
		paymentModalOpen,
		paymentPayload,
		processing,
		closePaymentModal,
		handleWalletPaid,
		handleCardInitiated,
		handleProcessingSuccess,
		handleProcessingFailed,
	} = useSubmitServiceRequestPage(serviceRequestId);

	if (pageState === "loading") {
		return <CustomerLoader subtitle="Loading service request..." />;
	}

	if (pageState === "error") {
		return (
			<CustomerPageLayout showBackButton={false}>
				<div className={styles.frame}>
					<SubmitRequestAppBar onBack={goBack} totalSteps={1} activeStep={0} />
					<FailedToLoadData />
				</div>
			</CustomerPageLayout>
		);
	}

	// Verifying a wallet/card payment (the processor renders its own full-screen overlay).
	if (processing) {
		return (
			<PaymentProcessor
				themed
				paymentUrl={processing.paymentUrl}
				reference={processing.reference}
				skipRedirect={processing.skipRedirect}
				onSuccess={handleProcessingSuccess}
				onFailed={handleProcessingFailed}
				onBack={handleProcessingFailed}
				statusMessages={{
					validating: { title: "Submitting service request", subtitle: "Please wait while we confirm your payment" },
					processing: { title: "Submitting service request", subtitle: "This will only take a moment" },
				}}
			/>
		);
	}

	if (currentStep === "loading") {
		return <CustomerLoader subtitle="Submitting service request..." />;
	}

	if (currentStep === "success") {
		return <SubmitRequestSuccessStep onBackToHome={() => navigate("/dashboard")} />;
	}

	if (currentStep === "failed") {
		return (
			<CustomerPageLayout showBackButton={false}>
				<div className={styles.frame}>
					<SubmitRequestAppBar onBack={goBack} totalSteps={totalSteps} activeStep={activeStep} />
					<FailedToLoadData text="Submit Service Request Failed" />
				</div>
			</CustomerPageLayout>
		);
	}

	return (
		<CustomerPageLayout showBackButton={false}>
			<div className={styles.frame}>
				<SubmitRequestAppBar onBack={goBack} totalSteps={totalSteps} activeStep={activeStep} />

				<ServiceRequestHeader tagName={tagName} sectionTitle={sectionTitle} description={headerSubtitle} />

				<div className={`${styles.scrollArea} ${currentStep === "dynamicForm" ? styles.scrollAreaForm : ""}`}>
				{currentStep === "dynamicForm" && currentSectionDefinition && (
					<CardFrame>

						<div className={styles.cardBody}>
							<DynamicForm
								key={currentSection}
								sectionDefinition={currentSectionDefinition}
								preselectedFormValues={currentSectionValues}
								isSubmitted={isSubmitted}
								onValuesChange={handleValuesChange}
							/>
						</div>
					</CardFrame>
				)}

				{currentStep === "confirmation" && serviceRequest && (
					<SubmitRequestConfirmationStep serviceRequest={serviceRequest} sectionValues={allSectionValues} />
				)}
			</div>

			<div className={styles.bottomBar}>
				<Button variant="primary" fullWidth onClick={goNext} className={styles.nextButton}>
					{bottomButtonLabel}
				</Button>
			</div>
		</div>

			{
		paymentModalOpen && price && paymentPayload && (
			<ServiceRequestPaymentModal
				price={price}
				payload={paymentPayload}
				onClose={closePaymentModal}
				onWalletPaid={handleWalletPaid}
				onCardInitiated={handleCardInitiated}
			/>
		)
	}
		</CustomerPageLayout >
	);
};
